import {Grid, Table, TableBody, TableContainer} from '@mui/material';
import _ from 'lodash';
import moment from 'moment';
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import config from '../../../../app/config';
import EcdsObsRow from './ecdsObsRow';
import {airOrOxygen} from './ecds_data';
import {mapObservationFromAPI} from '../observation/obsUtils';
import MessageBox from '../../../../util/messageBox';
import {useDispatch, useSelector} from 'react-redux';
import {addNoteForPatient} from '../../../../store/reducers/notes';
import API from '../../../../app/api';
import Notes from '../../../../util/notes';
import {hasPermission, Permissions} from '../../../../store/reducers/user';
import {useStylesECDS} from './ecdsStyle';
import {codedFinding} from '../../../../store/reducers/dicts';
import InformationBox from '../../../../components/other/informationBox';
import AgyleAccordion from '../../../../components/ui/accordion/agyleAccordion';
import {AgyleTable, createAgyleTableColumn} from '../../../../components/table/AgyleTable';
import useOpenState from '../../../../hooks/useOpenState';
import AgyleDialog from '../../../../components/modal/agyleDialog';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import EcdsTableHeader from './components/ecdsTableHeader';
import {additionalData} from '../../../../store/reducers/additionalData';
import uuidv4 from 'uuid/v4';
import {getTableScrollbar} from '../../../../util/scrollbar';

const PREGNANCY_SNOMED_CODES = ['167252002', '67900009'];

const CodedClinicalFindings = (props) => {
    const {t} = useTranslation();
    const classes = useStylesECDS();
    const dispatch = useDispatch();
    const {version} = props;

    // Additional Patient Data
    const patientAddData = useSelector(additionalData);

    // Table Data
    const [observation, setObservation] = useState(_.isEmpty(props.obs) ? [] : props.obs);
    const [findings, setFindings] = useState([]);
    const [rockwood, setRockwood] = useState([]);
    const [pregnancy, setPregnancy] = useState([]);
    const canAmendTime = dispatch(hasPermission(Permissions.AGYLE_IS_ECDS_ADMIN));

    // ECDS Data Set
    const [ecds, setEcds] = useState([]);

    // Dictionary for mapping SnomedCode
    const dictionary = useSelector(codedFinding);

    // Accordion
    const [expanded, setExpanded] = useState(false);

    // Opener
    const [isRockwoodEditOpen, openRockwoodEdit, closeRockwoodEdit] = useOpenState(false, false);
    const [isConfirmOpen, openConfirm, closeConfirm] = useOpenState(false, false);

    // Data for edit rockwood
    const [selectedRockwood, setSelectedRockwood] = useState(false);
    const errorPicker = useRef(null);
    const [dataForm, setDataForm] = useState({touch: false});

    useEffect(() => {
        setObservation(props.obs);
        setEcds([]);
        if (!_.isEmpty(props.obs)) {
            _.forEach(props.obs, obs => {
                const mapped = mapObservationFromAPI(obs);

                addToTable(mapped);
            });
        } else {
            setFindings([]);
        }

        if (!_.isEmpty(props.patient?.studies?.rockwood?.list)) {
            addToTableRockwood(props.patient?.studies?.rockwood?.list);
        } else {
            setRockwood([]);
        }
        if (version === 'V4') addToTablePregnancy();
    }, [props.obs, props.patient, patientAddData]);

    useEffect(() => {
        props.setData(ecds);
    }, [ecds]);

    const addToTableRockwood = (list) => {
        const findSnomedCode = (score) => {
            const dict = _.find(dictionary, element => {
                return element.ecdsVersion === version && _.startsWith(element.__description, `Clinical Frailty Scale level ${score}`);
            });
            return dict?.snomedCode;
        };

        let tableRockwood = [];
        let ecdsTable = [];
        _.forEach(list, rockwood => {
            const dict = _.find(dictionary, element => {
                return element.ecdsVersion === version && _.startsWith(element.__description, `Clinical Frailty Scale level ${rockwood?.scale?.score
                    ? rockwood?.scale?.score : rockwood?.scale?.__code}`);
            });

            const table = {
                id: rockwood.id,
                category: 'Clinical Frailty Scale level',
                value: dict ? dict?.__description
                    : rockwood?.scale?.score ? rockwood?.scale?.score : rockwood?.scale?.__code,
                timestamp: moment(rockwood.date, config.defaultDateTimeFormat).isValid()
                    ? rockwood.date : moment(rockwood.date).format(config.defaultDateTimeFormat),
            };
            tableRockwood.push(table);

            // For old data structure, new Rockwood scale will have SnomedCode
            const snomedCode = rockwood?.scale?.snomedCode ? rockwood?.scale?.snomedCode : findSnomedCode(rockwood?.scale?.score);

            if (snomedCode) {
                const data = {
                    CodedFinding_SnomedCt: snomedCode,
                    CodedFinding_TimeStamp: moment(rockwood.date).utc().format(config.ecdsDateTimeFormat),
                };
                ecdsTable.push(data);
            }
        });

        setRockwood(tableRockwood);
        setEcds(prevState => [
            ...prevState,
            ...ecdsTable,
        ]);
    };

    const addToTablePregnancy = () => {
        const data = [];
        const inv = _.cloneDeep(props.patient?.triage?.investigations);
        const filtered = _.filter(inv, i => PREGNANCY_SNOMED_CODES.includes(i?.value?.snomedCode));
        const addData = _.filter(patientAddData?.other?.urinalysis, uri => uri?.attendanceId === props.patient?.admission?.__id && !uri?.deleted && uri?.bhcg !== 'notTested');
        if (!_.isEmpty(addData) || !_.isEmpty(filtered)) {
            let status;
            let timeStamp;
            if (!_.isEmpty(filtered)) {
                const confirm = _.some(filtered, i => i.procedureTimestamp);
                const sorts = _.sortBy(filtered, ['requestedTimestamp']);
                timeStamp = sorts?.[0]?.requestedTimestamp;
                status = confirm ? 'confirm' : 'unknown';
            }

            if (!_.isEmpty(addData)) {
                const sorts = _.orderBy(addData, ['when'], ['desc']);
                const fisrt = sorts?.[0];
                if (!timeStamp || (timeStamp && moment(timeStamp).isAfter(moment(fisrt?.when)))) {
                    timeStamp = fisrt?.when;
                    status = fisrt?.bhcg === 'positive' ? 'confirm' : 'notConfirm';
                }
            }
            const snomedObject = getPregnancyCode(status);

            data.push({
                id: uuidv4() + '',
                category: 'Pregnancy',
                value: snomedObject?.__description || '-',
                timestamp: timeStamp ? moment(timeStamp).format(config.defaultDateTimeFormat) : '-',
            });
            const ecdsTable = [{
                CodedFinding_SnomedCt: snomedObject?.snomedCode || null,
                CodedFinding_TimeStamp: timeStamp ? moment(timeStamp).utc().format(config.ecdsDateTimeFormat) : null,
            }];
            setEcds(prevState => [
                ...prevState,
                ...ecdsTable,
            ]);
        }
        setPregnancy(data);
    };

    const getPregnancyCode = (status) => {
        const dict = _.filter(dictionary, {group: 'Preg_status'});
        // eslint-disable-next-line default-case
        switch (status) {
            case 'confirm':
                return _.find(dict, {__description: 'Pregnancy'});
            case 'notConfirm':
                return _.find(dict, {__description: 'Not pregnant'});
            case 'unknown':
                return _.find(dict, {__description: 'Pregnancy status unknown'});
        }
    };

    const createRow = (item, title, field) => {
        if (item?.status === 'completed') {
            const value = _.find(airOrOxygen, {id: item.airType});
            return {
                field: field,
                name: title,
                value: value?.value,
                timestamp: item?.acceptDate,
            };
        }
    };

    // Transform data to Table
    const addToTable = (obs) => {
        const airOrOxygen = createRow(obs.airOxygen, 'SpO₂', 'airOxygen');

        let tests = [
            airOrOxygen,
        ];

        tests = tests.filter(function (element) {
            return element !== undefined;
        });

        if (!_.isEmpty(tests)) {
            const obj = {
                id: obs.__id,
                tests: tests,
            };

            createECDSData(tests);

            setFindings(prevState => [
                ...prevState,
                obj,
            ]);
        } else {
            const obj = {
                id: obs.__id,
                tests: [{
                    name: 'No Findings for this observation',
                    value: '-',
                    timestamp: '',
                }],
            };

            setFindings(prevState => [
                ...prevState,
                obj,
            ]);
        }
    };

    // Transform data for ECDS data format
    const createECDSData = (obs) => {
        let arr = [];
        if (!_.isEmpty(obs)) {
            _.forEach(obs, ecds => {
                const dict = _.find(dictionary, {ecdsVersion: version, __description: ecds?.value});
                if (dict) {
                    arr.push({
                        CodedFinding_SnomedCt: dict?.snomedCode,
                        CodedFinding_TimeStamp: moment(ecds.timestamp).utc().format(config.ecdsDateTimeFormat),
                    });
                }
            });
        }
        setEcds(prevState => [
            ...prevState,
            ...arr,
        ]);
    };

    // Find assessments for table
    const findAssessments = (id) => {
        let arr = [];
        if (!_.isEmpty(findings)) {
            arr = _.find(findings, {id: id})?.tests;
        }
        return arr || [];
    };

    // Accordion handler
    const accordionChangeHandler = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    // Render Obs Table
    const renderObs = () => {
        if (!_.isEmpty(observation)) {
            return (
                <Grid container direction="row" style={{marginTop: 1}}>
                    <Grid item xs={12}>
                        <TableContainer className={getTableScrollbar()}>
                            <Table>
                                <EcdsTableHeader/>

                                <TableBody>
                                    {observation.map((obs, index) => (
                                        <EcdsObsRow
                                            findings
                                            index={index}
                                            key={obs.__id}
                                            obs={{
                                                id: obs.__id,
                                                date: obs.examinationEnd ? moment(obs.examinationEnd).format(config.defaultDateTimeFormat) : '-',
                                                by: obs.completedByName ? obs.completedByName : '-',
                                                status: obs.status ? _.startCase(obs.status) : '-',
                                            }}
                                            innerText="Findings"
                                            innerColumns={['Name', 'Value', 'Timestamp']}
                                            innerRow={findAssessments(obs.__id)}
                                            editObs={props.editObs}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            );
        } else {
            return (
                <InformationBox type="info">
                    {t('Observations have not been made.')}
                </InformationBox>
            );
        }
    };

    const saveRockwood = () => {
        closeRockwoodEdit();
        closeConfirm();
        let clone = _.cloneDeep(props.patient?.studies?.rockwood?.list);
        let cloneRockwood = _.cloneDeep(rockwood);
        let orgTime;

        const getNote = () => {
            return Notes.getClinicalFrailtyScoreEditNote(dataForm, orgTime);
        };

        if (dataForm.timestamp) {
            _.forEach(clone, (test, index) => {
                if (test.id === selectedRockwood.id) {
                    let orgTest = test;
                    orgTime = orgTest.date;
                    orgTest.date = moment(dataForm.timestamp).toISOString();
                    clone[index] = orgTest;
                }
            });

            _.forEach(cloneRockwood, (test, index) => {
                if (test.id === selectedRockwood.id) {
                    let orgTest = test;
                    orgTest.timestamp = moment(dataForm.timestamp).format(config.defaultDateTimeFormat);
                    cloneRockwood[index] = orgTest;
                }
            });

            setRockwood(cloneRockwood);

            const newObj = [{
                name: 'studies.rockwood',
                value: {
                    list: [...clone],
                },
            }];

            API.updatePatientInWard(props.patient.__id, [
                {
                    'studies.rockwood': {
                        list: [...clone],
                    },
                },
            ]).then((response) => {
                if (response.data) {
                    props.modifyPatient(newObj);
                    dispatch(addNoteForPatient(props.patient?.patient.__id, {
                        note: getNote(),
                        noteType: 'studies.rockwood',
                        patientsInWardsRef: [props.patient.__id],
                        episodeId: props.patient?.admission?.consultantEpisodeId || null,
                    }, props.patient.__id));
                }
            });
        }
    };

    // Amend Rockwood Time stamp
    const amendRockwood = (row) => {
        setSelectedRockwood(row);
        setDataForm(prevState => ({
            ...prevState,
            timestamp: moment(row.timestamp, config.defaultDateTimeFormat).isValid()
                ? moment(row.timestamp, config.defaultDateTimeFormat) : row.timestamp,
        }));
        openRockwoodEdit();
    };

    const isInvalid = () => {
        return errorPicker !== null && !dataForm.timestamp;
    };

    // Render Rockwood Table
    const renderRockwood = () => {
        const rockwoodColumns = [
            createAgyleTableColumn('no', 'No.', 1, null, 'count', 'notSort', 'medium', 'center', true, false, null, null, null),
            createAgyleTableColumn('name', 'Name', 2, 'category', 'string', '', 'medium', 'left', true, false, null, null, null),
            createAgyleTableColumn('value', 'Value', 3, 'value', 'string', '', 'medium', 'left', true, false, null, null, null),
            createAgyleTableColumn('timestamp', 'Timestamp', 4, 'timestamp', 'string', '', 'medium', 'left', true, false, null, null, null),
            // createAgyleTableColumn('action', 'Amend Timestamp', 5, null, 'action', '', 'medium', 'center', true, true, null, null, null),
        ];

        if (!_.isEmpty(rockwood)) {
            return (
                <AgyleTable
                    columns={rockwoodColumns}
                    data={rockwood}
                    editItem={amendRockwood}
                    disabledAction={!canAmendTime}
                    edRefresh
                    disableFilter
                />
            );
        } else {
            return (
                <InformationBox type="info">
                    {t('A clinical frailty scale has not been completed.')}
                </InformationBox>
            );
        }
    };

    // Render Pregnancy Table
    const renderPregnancy = () => {
        const columns = [
            createAgyleTableColumn('no', 'No.', 1, null, 'count', 'notSort', 'medium', 'center', true, false, null, null, null),
            createAgyleTableColumn('name', 'Name', 2, 'category', 'string', '', 'medium', 'left', true, false, null, null, null),
            createAgyleTableColumn('value', 'Value', 3, 'value', 'string', '', 'medium', 'left', true, false, null, null, null),
            createAgyleTableColumn('timestamp', 'Timestamp', 4, 'timestamp', 'string', '', 'medium', 'left', true, false, null, null, null),
        ];

        if (!_.isEmpty(pregnancy)) {
            return (
                <AgyleTable
                    columns={columns}
                    data={pregnancy}
                    edRefresh
                    disableFilter
                />
            );
        } else {
            return (
                <InformationBox type="info">
                    {t('No pregnancy test has been done.')}
                </InformationBox>
            );
        }
    };

    if (props.accordion !== 'Clinical Findings') return null;

    return (
        <div className={classes.root}>
            <AgyleAccordion
                expanded={expanded === 'obs'}
                onChange={accordionChangeHandler('obs')}
                label={t('Observation Air or Oxygen')}
            >
                {renderObs()}
            </AgyleAccordion>

            <AgyleAccordion
                expanded={expanded === 'rockwood'}
                onChange={accordionChangeHandler('rockwood')}
                label={t('Clinical Frailty Scale')}
            >
                {renderRockwood()}
            </AgyleAccordion>

            {version === 'V4' &&
                <AgyleAccordion
                    expanded={expanded === 'pregnancy'}
                    onChange={accordionChangeHandler('pregnancy')}
                    label={t('Pregnancy')}
                >
                    {renderPregnancy()}
                </AgyleAccordion>
            }

            <AgyleDialog
                open={isRockwoodEditOpen}
                onClose={closeRockwoodEdit}
                title={t('Update Clinical Frailty Scale Timestamp')}
                width="md"
                fullWidth
                onSave={openConfirm}
                disableSubmit={isInvalid()}
            >
                <AgyleDatePicker
                    label={t('Time of examination')}
                    value={dataForm.timestamp}
                    type="dateTime"
                    onChange={event => setDataForm({
                        touch: true,
                        timestamp: moment(event).toISOString(),
                    })}
                    maxDateTime={moment()}
                    onError={event => errorPicker.current = event}
                    error={dataForm.timestamp === null}
                    size="large"
                />
            </AgyleDialog>

            <MessageBox
                open={isConfirmOpen}
                title={t('Confirmation')}
                haveAccept={{yes: t('Yes'), no: t('No')}}
                onAccept={saveRockwood}
                onClose={closeConfirm}
                message={t('Are you sure you want to save new timestamp?')}
            />
        </div>
    );
};

export default CodedClinicalFindings;
